import type { AircraftDoc, FlightDoc, BillingCycleDoc } from './types';

export function fmtUSD(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
export function fmtHours(n: number) {
  return `${n.toFixed(1)} hrs`;
}

const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();

// Move one cycle forward/back from `date`, landing on the anchor day with real
// last-day clamping (a 31st anchor lands on Feb 28 / Apr 30, etc.).
function addCycle(date: Date, anchorDay: number, dir: number) {
  const a = Math.min(31, Math.max(1, anchorDay || 1));
  let m = date.getMonth() + dir;
  let y = date.getFullYear();
  y += Math.floor(m / 12);
  m = ((m % 12) + 12) % 12;
  return new Date(y, m, Math.min(a, daysInMonth(y, m)));
}

export function cycleWindow(billingDayOfMonth: number, ref: Date = new Date()) {
  // The billing cycle containing `ref`, anchored to billingDayOfMonth with real
  // month-length clamping. MUST stay identical to functions/index.js cycleWindow
  // so the displayed cycle and the actual charge cycle never diverge.
  const a = Math.min(31, Math.max(1, billingDayOfMonth || 1));
  const y = ref.getFullYear();
  const m = ref.getMonth();
  const d = ref.getDate();
  const clampedThis = Math.min(a, daysInMonth(y, m));
  const start =
    d >= clampedThis ? new Date(y, m, clampedThis) : addCycle(new Date(y, m, clampedThis), a, -1);
  const end = addCycle(start, a, 1);
  return { start, end };
}

export interface CycleSummary {
  start: Date;
  end: Date;
  hoursFlown: number;
  charges: number;
  daysRemaining: number;
  totalDays: number;
  flights: FlightDoc[];
  // Lease §5.2: no monthly minimum, no rollover, nothing owed for a cycle you
  // don't fly. A cycle is just Hobbs hours × rate, plus any nights away (§5.3).
  rate: number;
  hoursCharge: number;
  awayCharge: number;
  totalCharged: number;
}

// The  Hobbs-hr a given pilot actually pays — mirrors the server (functions/index.js
// onFlightLogged): promo plan (CFIs) = aircraft.cfiDiscountRate, comped = $0, everyone
// else the standard rate. Every pilot-facing rate display must go through this.
export function rateFor(profile: { plan?: string; billingProfile?: string } | null | undefined, aircraft: AircraftDoc): number {
  if (!profile) return aircraft.hourlyRate;
  if (profile.billingProfile === 'owner' || profile.plan === 'comped') return 0;
  if (profile.plan === 'promo') return aircraft.cfiDiscountRate ?? 75;
  return aircraft.hourlyRate;
}

export function summarizeCurrentCycle(args: {
  flights: FlightDoc[];
  aircraft: AircraftDoc;
  billingDayOfMonth?: number;
  ref?: Date;
  rate?: number; // pilot's own rate (rateFor); defaults to the standard rate
}): CycleSummary {
  const { start, end } = cycleWindow(args.billingDayOfMonth ?? 1, args.ref ?? new Date());
  const inCycle = args.flights.filter((f) => {
    const d = new Date(f.date);
    return d >= start && d < end;
  });
  const hoursFlown = +inCycle.reduce((s, f) => s + f.hoursFlown, 0).toFixed(1);
  const charges = +inCycle.reduce((s, f) => s + f.totalCharge, 0).toFixed(2);
  const rate = args.rate ?? args.aircraft.hourlyRate;
  const hoursCharge = +(hoursFlown * rate).toFixed(2);
  const awayCharge = +inCycle.reduce((s, f) => s + (f.awayFromBaseCharge || 0), 0).toFixed(2);
  const totalCharged = +(hoursCharge + awayCharge).toFixed(2);
  const now = args.ref ?? new Date();
  const totalDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const daysRemaining = Math.max(0, Math.round((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  return {
    start,
    end,
    hoursFlown,
    charges,
    daysRemaining,
    totalDays,
    flights: inCycle,
    rate,
    hoursCharge,
    awayCharge,
    totalCharged,
  };
}

// Estimate the immediate charge for ONE new flight: its Hobbs hours at the rate,
// plus its own away-from-base charge. Mirrors the server's reconcile delta (the
// server is authoritative — this is an estimate for the pre-charge confirmation).
export function projectFlightCharge(args: {
  aircraft: AircraftDoc;
  hoursFlown: number;
  awayCharge: number;
}): { hoursFlown: number; hoursCharge: number; awayCharge: number; estCharge: number } {
  const hoursFlown = +(args.hoursFlown || 0).toFixed(1);
  const hoursCharge = +(hoursFlown * args.aircraft.hourlyRate).toFixed(2);
  const awayCharge = +(args.awayCharge || 0).toFixed(2);
  return { hoursFlown, hoursCharge, awayCharge, estCharge: +(hoursCharge + awayCharge).toFixed(2) };
}

export function pastCycles(args: {
  flights: FlightDoc[];
  aircraft: AircraftDoc;
  billingDayOfMonth?: number;
  ref?: Date;
  count?: number;
  rate?: number;
}): BillingCycleDoc[] {
  const rate = args.rate ?? args.aircraft.hourlyRate;
  const cycles: BillingCycleDoc[] = [];
  const today = args.ref ?? new Date();
  const anchor = args.billingDayOfMonth ?? 1;
  const { start: currentStart } = cycleWindow(anchor, today);
  let end = currentStart;
  let start = addCycle(currentStart, anchor, -1);
  for (let i = 1; i <= (args.count ?? 6); i++, end = start, start = addCycle(start, anchor, -1)) {
    const inCycle = args.flights.filter((f) => {
      const d = new Date(f.date);
      return d >= start && d < end;
    });
    if (inCycle.length === 0 && cycles.length > 0) continue;
    const hoursFlown = +inCycle.reduce((s, f) => s + f.hoursFlown, 0).toFixed(1);
    const awayCharge = inCycle.reduce((s, f) => s + (f.awayFromBaseCharge || 0), 0);
    const total = +(hoursFlown * rate + awayCharge).toFixed(2);
    cycles.push({
      cycleId: start.toISOString().slice(0, 10), // YYYY-MM-DD cycle start → drives the statement view
      lesseeUid: '',
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
      hoursFlown,
      totalCharges: total,
      status: 'closed',
    });
  }
  return cycles;
}
